import styled from 'styled-components';
import { Headline500Skin } from '../../../atoms/Headline/HeadlineSkins';
import CTA from '../../../atoms/CTA';
import { CTAIconPosition } from '../../../atoms/CTA/CTA.constants';
import { getAddressFromPayload } from '~/helpers/getFormattedAddress';
import { CopyStandardSkin } from '../../../atoms/Copytext/CopytextSkins';
import LinkedIcon from './LinkedIcon';
import { mq } from '~/styles/grid';
import getWhatsAppLink from '~/helpers/getWhatsAppLink';

const topMargin = 20;

const Container = styled.div`
  @media (${mq.smDown}) {
    margin-top: 5px;
  }
`;

const Name = styled.div`
  ${Headline500Skin}

  span + span {
    margin-left: 8px;
  }
`;

const TitleContainer = styled.div`
  margin-bottom: ${topMargin}px;
`;

const AddressLine = styled.span`
  ${CopyStandardSkin};
  display: block;
  margin: 0;
  color: ${(props) => props.theme.color.gray600};
`;

const Contacts = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${topMargin}px;
`;

const StyledLinkedIcon = styled(LinkedIcon)`
  border-right: 1px solid ${(props) => props.theme.color.gray500};
  color: ${(props) => props.theme.color.primary800};
  &:last-child {
    border-right: none;
  }
`;

const SocialNetworks = styled.div`
  margin-top: ${topMargin}px;
  display: flex;
`;

/**
 * @param {ContactInfoBlockProps} props
 * @returns {React.ReactElement} The rendered component
 */
const ContactInfoBlock = ({
  formattedName,
  title,
  organization,
  streetAddress,
  extendedAddress,
  locality,
  region,
  postalCode,
  countryName,
  countryCode,
  email,
  telephoneNumber,
  mobileNumber,
  websiteUrl,
  socialProfileLinkedIn,
  socialProfileXing,
  whatsAppNumber,
  isBrand,
}) => {
  const addressArray = getAddressFromPayload({
    streetAddress,
    extendedAddress,
    postalCode,
    locality,
    region,
    countryName,
    countryCode,
  });
  return (
    <Container>
      {formattedName && (
        <TitleContainer>
          <Name className="contactName">{formattedName}</Name>
          {title && <AddressLine>{title}</AddressLine>}
        </TitleContainer>
      )}
      <div>
        {organization && <AddressLine>{organization}</AddressLine>}
        {addressArray.map((lineContent) => (
          <AddressLine key={lineContent.join()}>
            {lineContent.join(' ')}
          </AddressLine>
        ))}
      </div>
      <Contacts>
        {websiteUrl && (
          <CTA
            text={websiteUrl}
            href={websiteUrl}
            title={websiteUrl}
            icon="ARROW_BOLD"
            iconPos={CTAIconPosition.Before}
            skin="TERTIARY"
            isBrand={isBrand}
          />
        )}
        {email && (
          <CTA
            text={email}
            href={`mailto:${email}`}
            title={email}
            icon="MAIL"
            iconPos={CTAIconPosition.Before}
            skin="TERTIARY"
            isBrand={isBrand}
          />
        )}
        {telephoneNumber && (
          <CTA
            text={telephoneNumber}
            href={`tel:${telephoneNumber}`}
            title={telephoneNumber}
            icon="PHONE"
            iconPos={CTAIconPosition.Before}
            skin="TERTIARY"
            isBrand={isBrand}
          />
        )}
        {mobileNumber && (
          <CTA
            text={mobileNumber}
            href={`tel:${mobileNumber}`}
            title={mobileNumber}
            icon="SMARTPHONE"
            iconPos={CTAIconPosition.Before}
            skin="TERTIARY"
            isBrand={isBrand}
          />
        )}
      </Contacts>
      <SocialNetworks>
        {socialProfileXing && (
          <StyledLinkedIcon href={socialProfileXing} type="XING" />
        )}
        {socialProfileLinkedIn && (
          <StyledLinkedIcon href={socialProfileLinkedIn} type="LINKEDIN" />
        )}
        {whatsAppNumber && (
          <StyledLinkedIcon
            href={getWhatsAppLink(whatsAppNumber)}
            type="WHATSAPP"
          />
        )}
      </SocialNetworks>
    </Container>
  );
};

export default ContactInfoBlock;
